import { useEffect, useState } from 'react';
import {
  IUpdateMessagesPayload,
  selectIsPopupClosed,
  selectIsPopupLoaded,
  selectIsPopupOpenedFromEditor,
  updateMessages,
} from '../modules/messages';
import { useDispatch, useSelector } from 'react-redux';
import { IProductTags, ITaggedProduct } from '../../../shared/types/Product';
import { MessageService } from '../../../message-service';
import { globalConfig } from '../../../shared/config/config';
import { IPost } from '../../../shared/types/Post';
import { postsRelationshipsSelector } from '../modules/posts';
import { ThemeNames } from '../components/theme/theme.utils';

export const usePopup = ({ theme, products, posts, addPostConfig, addReviewConfig, tintAnalyticsTracker }) => {
  const { personalization, meta, config, socialFeeds, customFontPopup } = theme;
  const dispatch = useDispatch();
  const isPopupOpenedFromEditor = useSelector(selectIsPopupOpenedFromEditor);
  const isPopupClosed = useSelector(selectIsPopupClosed);
  const isPopupLoaded = useSelector(selectIsPopupLoaded);
  const setMessages = (payload: IUpdateMessagesPayload) => dispatch(updateMessages(payload));
  const [shouldOpenAddPost, setShouldOpenAddPost] = useState(
    config.addPostOpen !== 'null' && config.addPostOpen !== 'false' && !!config.addPostOpen
  );
  const postsRelationships = useSelector(postsRelationshipsSelector);
  const hasPublicPostingSocialFeed = Object.keys(socialFeeds).includes('public_post');
  const hasReviewsRatingsSocialFeed = Object.keys(socialFeeds).includes('reviews');
  const postsFilteredByTheme = (posts: IPost[], theme?: string) => {
    if (!theme) {
      return posts;
    }

    if (theme === ThemeNames.VIDEO_SLIDER) {
      return posts.filter(post => post.attributes.video_url);
    }

    if (theme === ThemeNames.TABLE) {
      return getPostsWithSiblings().filter(post => post.attributes.image_url || post.attributes.video_url);
    }

    return posts;
  };

  useEffect(() => {
    if (isPopupOpenedFromEditor) {
      posts.length ? sendOpenPopupMessage(0, getCurrentProducts(posts[0].relationships.product_tags.data), posts) : null;
      setMessages({ key: 'isPopupOpenedFromEditor', value: false });
    }
  }, [isPopupOpenedFromEditor]);

  const getCurrentProducts = (productTags: ReadonlyArray<IProductTags>) => {
    if (!productTags || productTags.length === 0) return [];
    const productIds = productTags?.map((product: IProductTags) => product.id);

    return mapProductList()?.filter((taggedProduct: ITaggedProduct) => {
      return productIds?.find((id: string) => id === taggedProduct.productTagId);
    });
  };
  const isTablePersonalization = personalization.attributes.theme === 'table';

  useEffect(() => {
    if (shouldOpenAddPost) {
      setShouldOpenAddPost(false);
      setTimeout(() => {
        if (isTablePersonalization) {
          hasReviewsRatingsSocialFeed && addReviewConfig.onClick();
        } else {
          hasPublicPostingSocialFeed && addPostConfig.onClick();
        }
      }, 500);
    }
  }, [shouldOpenAddPost, setShouldOpenAddPost, personalization.attributes.theme, addPostConfig, addReviewConfig]);

  const openPopup = (post: IPost): void => {
    const filteredPosts = postsFilteredByTheme(posts, personalization.attributes.theme);

    filteredPosts.length
      ? sendOpenPopupMessage(
          filteredPosts.findIndex(elem => elem.id === post.id),
          getCurrentProducts(post.relationships.product_tags.data as ReadonlyArray<IProductTags>),
          filteredPosts
        )
      : null;
    tintAnalyticsTracker.track('embed_post_modal_opened', {
      post_id: post.id,
      media_type: post.attributes.media_type,
    });
  };

  const getPostsWithSiblings = () => {
    return posts.reduce((previousValue: IPost[], currentValue: IPost) => {
      const siblings = currentValue.relationships.siblings?.data?.map(
        sibling => postsRelationships?.find(e => e.id === sibling.id) as IPost
      );
      return [...previousValue, currentValue, ...siblings];
    }, []);
  };

  const mapProductList = () => {
    const assignTagsToProduct = products.productTags.map(productTag => ({
      ...productTag,
      product: products.productList.find(product => product.id === productTag.relationships.product.data.id),
    }));

    return assignTagsToProduct.map(product => ({
      height: product.attributes.height,
      width: product.attributes.width,
      x: product.attributes.x,
      y: product.attributes.y,
      productId: product.attributes.product_id,
      productUrl: product.product ? product.product.attributes.url : null,
      productTagId: product.id,
    }));
  };

  const sendOpenPopupMessage = (
    index: number,
    taggedProducts: ReadonlyArray<ITaggedProduct>,
    posts?: readonly IPost[]
  ) =>
    MessageService.send({
      type: globalConfig.postMessage.actions.openPopup,
      index,
      posts,
      taggedProducts,
      personalization,
      config,
      meta,
      socialFeeds,
      customFontPopup,
    });

  return {
    isPopupLoaded,
    isPopupClosed,
    openPopup,
  };
};
